import React, { useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import { useDidUpdate } from "hooks";

interface Option {
  title: string;
  handleClick: () => void | Promise<void>;
}

interface Props {
  options: Option[];
  disabled?: boolean;
  enableErrorIcon?: boolean;
}

export const SplitButton: React.FC<Props> = ({ options, disabled, enableErrorIcon }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<Option>(options[0]);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // Keep selected option updated
  useDidUpdate(() => {
    const updatedOption = options.find((item) => item.title === selectedOption.title);
    if (!updatedOption) return;
    setSelectedOption(updatedOption);
  }, [options]);

  const handleMenuItemClick = (option: Option) => {
    setSelectedOption(option);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <>
      <ButtonGroup disabled={disabled} sx={{ boxShadow: "none" }} variant="contained" ref={anchorRef}>
        <Button
          startIcon={enableErrorIcon ? <ErrorOutlineIcon sx={{ color: "#D93E3E" }} /> : undefined}
          size="md"
          onClick={() => selectedOption.handleClick()}
          sx={{ pl: "20px", whiteSpace: "nowrap" }}
        >
          {selectedOption.title}
        </Button>
        <Button sx={{ height: "100%", pr: "20px" }} size="sm" onClick={handleToggle}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1000
        }}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option) => (
                    <MenuItem
                      key={option.title}
                      selected={option.title === selectedOption.title}
                      onClick={() => handleMenuItemClick(option)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#DBE8F5"
                        }
                      }}
                    >
                      {option.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
